import { bind, chain, compose, isEmpty, property } from 'underscore';
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { FilterService } from '@bingads-webui/filter-service';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { useColumnsWithToolbarColumnChooser } from '@bingads-webui-campaign/hotel-column-chooser';
import { ContextualDetailsList, GridContext } from '@bingads-webui-grid/grid-context';
import { useCampaignGridDataSource } from '@bingads-webui-grid/campaign-data-source';
import { AppealByIDsForParentEntity, isAppealableStatus } from '@bingads-webui-campaign-react/editorial-appeal';
import { DisclaimerBanner } from '@bingads-webui-campaign-react/help';

import { buildColumns, defaultColumnKeys, getColumnCategories } from './columns';
import { getFilterConfig } from './filter-config';
import { getToolbarConfig } from './toolbar-config';
import { legacyI18nKeys } from './legacy-i18n';
import { getAssetGroupsEDM } from '../api/grid-data';
import { useDateRange } from '../common/p$dateRange';

const ToolbarShownItem = {
  Toolbar: 'Toolbar',
  ColumnChooser: 'ColumnChooser',
  FilterView: 'FilterView',
};

export const AssetGroupsGrid = ({
  classes,
  i18n: i18nWithTL,
  scope,
  serverContext: { CurrentAccount: { TimeZone } },
  permissions,
  preferenceService,
  isReadOnlyUser,
  appealOptions,
  onCreateAssetGroup,
  onEditAssetGroup,
  onEditAssetGroupStatus,
  onBulkEditAssetGroupsStatus,
  onRequestException,
  onInlineEditAssetGroup,
  onSwitchToSummaryMode,
  onSwitchToTableMode,
  onGenericError,
  onNavigateToAssetsPage,
  isUnifiedAppAds,
  isMobileAppCampaign,
}) => {
  const container = useRef(null);

  const isInitialized = useRef(false);
  const { state, dispatch } = useContext(GridContext);
  const { selectedItems, editableFilter } = state;

  const dateRange = useDateRange();

  const gridRefreshKey = useMemo(() => `${dateRange.StartDate}-${dateRange.EndDate}`, [dateRange]);
  useEffect(() => {
    dispatch({ type: 'REFRESH_GRID_DATA' });
  }, [dispatch, gridRefreshKey]);

  useCampaignGridDataSource({
    edm: getAssetGroupsEDM(scope.values),
    odataParams: {
      payload: {
        gridDataParameters: {
          DateRange: dateRange,
        },
      },
    },
    onError: onGenericError,
  });

  const i18n = useMemo(() => mergeI18n(
    buildLegacyI18n(i18nWithTL, legacyI18nKeys),
    i18nWithTL
  ), [i18nWithTL]);

  const [toolbarShownItem, setToolbarShownItem] = useState(ToolbarShownItem.Toolbar);

  const refreshGrid = useCallback(() => {
    dispatch({ type: 'REFRESH_GRID_DATA' });
  }, [dispatch]);

  const onEditAssetGroupStatusAndRefresh = useCallback((...args) => {
    onEditAssetGroupStatus(...args).then(refreshGrid);
  }, [refreshGrid, onEditAssetGroupStatus]);

  const onBulkEditAssetGroupsStatusAndRefresh = useCallback((status) => {
    onBulkEditAssetGroupsStatus(selectedItems, status).then(({ cancelled } = {}) => {
      if (!cancelled) {
        dispatch({ type: 'SET_ALL_SELECTED', payload: false });
        refreshGrid();
      }
    });
  }, [dispatch, onBulkEditAssetGroupsStatus, selectedItems, refreshGrid]);

  const onInlineEditAssetGroupAndRefresh = useCallback((...args) => (
    onInlineEditAssetGroup(...args).then(refreshGrid)
  ), [onInlineEditAssetGroup, refreshGrid]);

  const onRequestExceptionAndRefresh = useCallback((appealData) => {
    onRequestException(appealData, { onDone: refreshGrid });
  }, [onRequestException, refreshGrid]);

  const onRequestExceptionFromToolbar = useCallback(() => {
    const { values: { campaignId } } = scope;
    const ids = chain(selectedItems)
      .filter(compose(isAppealableStatus, property('DeliveryStatus')))
      .pluck('Id')
      .value();
    if (isEmpty(ids)) {
      // eslint-disable-next-line no-alert
      window.alert(i18n.getString(_TL_('None of the selected rows have the delivery status of Disapproved or Approved Limited.')));
      return;
    }
    const appealParams = new AppealByIDsForParentEntity(appealOptions, campaignId, ids);
    onRequestExceptionAndRefresh(appealParams);
  }, [i18n, scope, selectedItems, onRequestExceptionAndRefresh, appealOptions]);

  const toolbarConfig = useMemo(() => getToolbarConfig({
    i18n,
    isReadOnlyUser,
    onBulkEditAssetGroupsStatus: onBulkEditAssetGroupsStatusAndRefresh,
    onRequestException: onRequestExceptionFromToolbar,
    onCreateAssetGroup,
    onSwitchToSummaryMode,
    onSwitchToTableMode,
  }), [i18n, isReadOnlyUser, onBulkEditAssetGroupsStatusAndRefresh, onRequestExceptionFromToolbar, onCreateAssetGroup, onSwitchToSummaryMode, onSwitchToTableMode]);

  const toolbarEditor = useMemo(() => getToolbarEditor({ toolbarConfig }), [toolbarConfig]);

  const openColumnChooser = useCallback(() => {
    setToolbarShownItem(ToolbarShownItem.ColumnChooser);
  }, []);

  const {
    columns: columnKeys,
    ColumnChooser,
    ColumnChooserTrivia,
    initialColumns,
  } = useColumnsWithToolbarColumnChooser({
    defaultColumns: defaultColumnKeys,
    gridName: 'AssetGroupGrid',
    toolbarOptions: {
      toolbarEditor,
      openColumnChooser,
    },
  });

  useEffect(() => {
    const columns = buildColumns({
      i18n,
      isReadOnlyUser,
      selectedColumnKeys: columnKeys,
      appealOptions,
      onEditAssetGroup,
      onEditAssetGroupStatus: onEditAssetGroupStatusAndRefresh,
      onInlineEditAssetGroup: onInlineEditAssetGroupAndRefresh,
      onRequestException: onRequestExceptionAndRefresh,
      permissions,
      scope,
      onNavigateToAssetsPage,
      isUnifiedAppAds,
      isMobileAppCampaign,
    });

    dispatch({ type: 'UPDATE_SELECTED_COLUMNS', payload: columns });
  }, [i18n, isReadOnlyUser, onNavigateToAssetsPage, isMobileAppCampaign, isUnifiedAppAds, columnKeys, dispatch, onEditAssetGroup, onEditAssetGroupStatusAndRefresh, onInlineEditAssetGroupAndRefresh, onRequestExceptionAndRefresh, appealOptions, permissions, scope]);

  const onColumnChooserApply = useCallback(() => {
    setToolbarShownItem(ToolbarShownItem.Toolbar);
  }, []);

  const onColumnChooserCancel = useCallback(() => {
    setToolbarShownItem(ToolbarShownItem.Toolbar);
  }, []);

  const renderColumnChooser = useCallback(() => {
    const ColumnChooserComponent = ColumnChooser;
    return toolbarShownItem === ToolbarShownItem.ColumnChooser && (
      <ColumnChooserComponent
        categories={getColumnCategories({ i18n, permissions })}
        legacyI18n={i18n}
        selected={columnKeys}
        onApply={onColumnChooserApply}
        onCancel={onColumnChooserCancel}
      />);
  }, [ColumnChooser, columnKeys, i18n, onColumnChooserApply, onColumnChooserCancel, permissions, toolbarShownItem]);

  const resetToolbarVisibility = useCallback(({ forceHideToolbar }) => {
    if (forceHideToolbar) {
      setToolbarShownItem(ToolbarShownItem.FilterView);
    } else {
      setToolbarShownItem(ToolbarShownItem.Toolbar);
    }
  }, []);

  const onToolbarClear = useCallback(() => {
    dispatch({ type: 'SET_ALL_SELECTED', payload: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderToolbarContainer = useCallback(
    () => (<ToolbarContainer
      i18n={i18n}
      forceHideToolbar={toolbarShownItem !== ToolbarShownItem.Toolbar}
      onClear={onToolbarClear}
      showToolbarUnselected={selectedItems.length === 0}
      showToolbarSelected={selectedItems.length > 0}
      selectionCount={selectedItems.length}
      toolbarSelectedConfig={toolbarConfig.toolbarSelectedConfig}
      toolbarUnselectedConfig={toolbarConfig.toolbarUnselectedConfig}
    />),
    [i18n, toolbarShownItem, onToolbarClear, selectedItems, toolbarConfig.toolbarSelectedConfig, toolbarConfig.toolbarUnselectedConfig]
  );

  const filterConfig = useMemo(() => getFilterConfig({ i18n, preferenceService }), [i18n, preferenceService]);

  const filterService = useMemo(() => {
    const newFilterService = new FilterService(filterConfig);

    newFilterService.on('filter-state-changed', () => {
      const filterEntity = newFilterService.getCombinedFilter();
      const query = filterEntity ? FilterEntity.toOdata(filterEntity.query, filterEntity.odataOptions) : undefined;
      dispatch({ type: 'SET_EDITABLE_FILTER', payload: query });
    });

    return newFilterService;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  const renderFilterViewManager = useCallback(() => {
    const toolbarEditorUpdate = bind(toolbarEditor.update, toolbarEditor);

    return (
      <div>
        <FilterViewManagerWithGlobalFilterContext
          filterService={filterService}
          updateToolbar={toolbarEditorUpdate}
          resetToolbarVisibility={resetToolbarVisibility}
          legacyI18n={i18n}
          i18n={i18n}
          permissions={permissions}
          {...filterConfig}
        />
      </div>);
  }, [filterService, filterConfig, i18n, permissions, resetToolbarVisibility, toolbarEditor]);

  const toolbar = useMemo(
    () => (
      <React.Fragment>
        {renderToolbarContainer()}
        {renderFilterViewManager()}
        {renderColumnChooser()}
      </React.Fragment>
    ),
    [renderColumnChooser, renderFilterViewManager, renderToolbarContainer]
  );

  const noDataView = useMemo(() => (
    <div className="grid-no-data">
      {editableFilter ? i18n.getString(_TL_('There are no asset groups based on your filtered search.')) : i18n.getString(_TL_('There are no asset groups.'))}
    </div>
  ), [editableFilter, i18n]);

  useEffect(() => {
    async function init() {
      isInitialized.current = true;
      await initialColumns();
    }

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={container}>
      {state.selectedColumns.length > 0 && <ContextualDetailsList
        setKey="appads-asset-groups-grid"
        stickyHeader
        classes={classes}
        toolbar={toolbar}
        freezingColumnCount={2}
        placeholder={noDataView}
      />}
      <ColumnChooserTrivia />
      <DisclaimerBanner timezone={TimeZone} />
    </div>
  );
};

AssetGroupsGrid.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isReadOnlyUser: PropTypes.bool,
  permissions: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func,
    values: PropTypes.shape({
      aid: PropTypes.number,
      cid: PropTypes.number,
      campaignId: PropTypes.number,
      assetGroupId: PropTypes.number,
      assetGroupTableMode: PropTypes.bool,
    }),
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      TimeZone: PropTypes.number,
    }),
  }).isRequired,
  appealOptions: PropTypes.shape({}).isRequired,
  onCreateAssetGroup: PropTypes.func.isRequired,
  onEditAssetGroup: PropTypes.func.isRequired,
  onEditAssetGroupStatus: PropTypes.func.isRequired,
  onBulkEditAssetGroupsStatus: PropTypes.func.isRequired,
  onRequestException: PropTypes.func.isRequired,
  onInlineEditAssetGroup: PropTypes.func.isRequired,
  onSwitchToSummaryMode: PropTypes.func.isRequired,
  onSwitchToTableMode: PropTypes.func.isRequired,
  onGenericError: PropTypes.func.isRequired,
  onNavigateToAssetsPage: PropTypes.func.isRequired,
  isUnifiedAppAds: PropTypes.bool.isRequired,
  isMobileAppCampaign: PropTypes.bool.isRequired,
};

AssetGroupsGrid.defaultProps = {
  isReadOnlyUser: true,
};
